import { ReactNode, useEffect } from 'react';

import { Box, Button, Divider, Flex, Grid, Skeleton, Stack, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';

import { useStoreTempInClinicPdfMutation } from '@/store/slices/api/reportsApiSlice';
import formatDate from '@/utils/formatDate';
import AttachmentListItem from '@/components/PDFDropzone/AttachmentListItem';
import colors from '@/styles/colors';

import { DEVICE_STATUS_OPTIONS, DeviceStatus } from '@/pages/physician/analytics/constants';
import { CustomFileType } from '@/pages/physician/PatientOverviewPage/AddReportsModal';
import { LabelAndValue } from '@/types/custom';
import { MatchDevice } from '@/types/matchDevice';
import Dropdown from '@/ui/Dropdown';
import Dropzone from '@/ui/Dropzone';
import IconButton from '@/ui/IconButton';
import TextWrapper from '@/ui/TextWrapper';

import DeviceInfoFields from '../../../CreateDevice/DeviceInfoFields';
import { ReportFormValues } from '../index';
import styles from '../ReportCreateModal.module.scss';
import { VENDOR_MAPPING } from '@/pages/physician/PatientOverviewPage/types';

export function getDeviceStatusColor(status: string): string {
  const activeColor = colors.green[6]; // green
  const inactiveColor = colors.neutral[4]; // grey
  const statusLabel =
    DEVICE_STATUS_OPTIONS.find(
      (option) => option.value === (status as unknown as DeviceStatus)
    )?.label.toLocaleUpperCase() ?? '';

  return statusLabel === 'INACTIVE' || statusLabel === 'ENTERED IN ERROR'
    ? inactiveColor
    : activeColor;
}

export function isActiveReportDevice(status: unknown): boolean {
  const value = String(status ?? '').toLowerCase();
  return !(value === 'inactive' || value === 'entered in error' || value === '0' || value === '4');
}

export function getDeviceStatusLabel(status: string): string {
  const statusLabel =
    DEVICE_STATUS_OPTIONS.find(
      (option) => option.value === (status as unknown as DeviceStatus)
    )?.label.toLocaleUpperCase() ?? '';
  return statusLabel === 'INACTIVE' || statusLabel === 'ENTERED IN ERROR' ? 'INACTIVE' : 'ACTIVE';
}

const ManualTransmissionSection = ({
  control,
  setValue,
  patientData,
  isDeviceAdded,
  setIsDeviceAdded,
  setUploadedFiles,
  uploadedFiles,
  setUploadPdfLoading,
  vendor,
  setSelectedInClinicFile,
  transmissionType,
  setIsUploading,
  isUploading
}: {
  control: Control<ReportFormValues>;
  setValue: UseFormSetValue<ReportFormValues>;
  patientData: any;
  isDeviceAdded: boolean;
  setIsDeviceAdded: (val: boolean) => void;
  setUploadedFiles: (files: any) => void;
  uploadedFiles: CustomFileType[];
  setUploadPdfLoading: (val: boolean) => void;
  vendor?: string;
  setSelectedInClinicFile: (file: File | null) => void;
  transmissionType: 'remote' | 'in-clinic';
  setIsUploading: (val: boolean) => void;
  isUploading: boolean;
}) => {
  // comment out for Removing ORC changes
  // const MAX_FILES = transmissionType === 'in-clinic' ? 1 : 10;
  // const MAX_FILE_SIZE = transmissionType === 'in-clinic' ? 20 * 1024 * 1024 : 10 * 1024 ** 2;
  const MAX_FILES = 10;
  const [storeTempPdfMutation, { isLoading: uploadPdfLoading }] = useStoreTempInClinicPdfMutation();

  const showErrorNotification = (title: string, message?: string) => {
    notifications.show({ color: 'red', title, message: message || '' });
  };

  const deviceOptions: LabelAndValue[] =
    patientData?.matchDevices
      ?.filter(
        (device: MatchDevice) =>
          isActiveReportDevice(device.status) &&
          (transmissionType === 'remote'
            ? VENDOR_MAPPING[device.vendor]?.toLowerCase() !== 'sorin'
            : true)
      )
      .map((device: MatchDevice) => ({
        value: device.id,
        label: (
          <Flex align="center" justify="space-between" style={{ width: '100%' }}>
            <Box>
              <TextWrapper text={`${VENDOR_MAPPING[device.vendor]} ${device.type}`} size="sm" />
              <TextWrapper
                text={
                  device.modelNumber
                    ? `MN: ${device.modelNumber} • DOI: ${formatDate(device.implantDate, 'MM/DD/YYYY')}`
                    : `DOI: ${formatDate(device.implantDate, 'MM/DD/YYYY')}`
                }
                size="xs"
                c={'gray'}
              />
            </Box>
            <Box>
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 12,
                  color: getDeviceStatusColor(device.status ?? '')
                }}>
                {getDeviceStatusLabel(device.status ?? '')}
              </span>
            </Box>
          </Flex>
        )
      })) ?? [];

  const getS3Url = async (fileName: string): Promise<string> => {
    const { data } = await storeTempPdfMutation({ fileName, patientId: patientData?.id });
    return data?.preSignedUrl ?? '';
  };
  useEffect(() => {
    setUploadPdfLoading?.(uploadPdfLoading);
  }, [uploadPdfLoading]);

  const onDrop = async (fileList: FileList) => {
    const acceptedFiles = Array.from(fileList).filter((file) => file.type === 'application/pdf');
    const rejectedFiles = Array.from(fileList).filter((file) => file.type !== 'application/pdf');

    if (uploadedFiles.length + acceptedFiles.length + rejectedFiles.length > MAX_FILES) {
      showErrorNotification(`Too Many Files', "You can't attach more than ${MAX_FILES} files`);
      return;
    }

    const rejectedFilesWithStatus: CustomFileType[] = rejectedFiles.map((file) =>
      Object.assign(file, {
        status: false,
        id: crypto.randomUUID(),
        errors: [{ code: 'file-invalid-type', message: 'Unsupported file type' }]
      })
    );
    setUploadedFiles((prev: any) => [...prev, ...rejectedFilesWithStatus]);

    // comment out for Removing ORC changes
    // if (transmissionType === 'in-clinic') {
    //   const file = acceptedFiles[0]; // only allow 1
    //   setSelectedInClinicFile(file); // capture for payload
    //   const newFile: CustomFileType = Object.assign(file, {
    //     id: crypto.randomUUID(),
    //     status: true
    //   });

    //   setUploadedFiles((prev: any) => [...prev, newFile]);
    //   return;
    // }
    setIsUploading(true);

    try {
      const uploadPromises = acceptedFiles.map(async (file) => {
        const s3Url = await getS3Url(file.name);
        const result = await fetch(s3Url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/pdf',
            filename: file.name
          },
          body: file
        });
        if (!result.ok) return;

        const newFile: CustomFileType = Object.assign(file, {
          id: crypto.randomUUID(),
          status: true
        });
        setUploadedFiles((prev: any) => [...prev, newFile]);
      });

      await Promise.all(uploadPromises);
    } catch (error) {
      console.error('S3 upload failed', error);
    } finally {
      setIsUploading(false);
    }
  };

  const renderAttachments = () => {
    if (uploadPdfLoading || isUploading) {
      return <Skeleton visible={uploadPdfLoading || isUploading} h={40} />;
    }

    if (uploadedFiles.length > 0 || uploadPdfLoading) {
      console.log({ uploadedFiles });
      return (
        <Stack>
          <Title lh={0} size="h5" style={{ border: '1px solid gray.4', marginTop: '10px' }}>
            Added Attachments:
          </Title>
          <Box my={0}>
            <Divider pb={0} />
          </Box>
          <Stack gap="xs" style={{ maxHeight: 200, overflow: 'auto' }}>
            {uploadedFiles.map((file) => (
              <AttachmentListItem
                key={file.id}
                id={file.id}
                originalName={file.name}
                errors={file.errors}
                onDelete={(idToDelete) =>
                  setUploadedFiles((prev: any) => prev.filter((f: any) => f.id !== idToDelete))
                }
              />
            ))}
          </Stack>
        </Stack>
      );
    }
  };

  const getRequiredLabel = (label: string): ReactNode => (
    <>
      {label}
      {transmissionType === 'remote' && <span style={{ color: 'red' }}> *</span>}
    </>
  );

  return (
    <>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Controller
            control={control}
            name="deviceId"
            render={({ field }) => (
              <Dropdown
                label="Select Device"
                required
                labelVariant="regular"
                options={deviceOptions}
                value={deviceOptions.find((opt) => opt.value === field.value)}
                onChange={(option) => field.onChange(option?.value)}
                placeholder="Select Device"
                isDisabled={isDeviceAdded}
              />
            )}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 4 }} mt={20}>
          {!isDeviceAdded && (
            <Button
              variant="outline"
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setIsDeviceAdded(true);
              }}
              className={styles.addButton}>
              Add Device
            </Button>
          )}
        </Grid.Col>

        {isDeviceAdded && (
          <>
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Box ta="right">
                <IconButton
                  icon="trash"
                  onClick={() => {
                    setIsDeviceAdded(false);
                    setValue('vendor', '', { shouldValidate: true, shouldDirty: true });
                    setValue('type', '', { shouldValidate: true, shouldDirty: true });
                    setValue('serialNumber', '', { shouldValidate: true, shouldDirty: true });
                    setValue('modelNumber', '', { shouldValidate: true, shouldDirty: true });
                    setValue('modelName', '', { shouldValidate: true, shouldDirty: true });
                    setValue('implantDate', '', { shouldValidate: true, shouldDirty: true });
                  }}
                  label="Delete"
                />
              </Box>
            </Grid.Col>
          </>
        )}
      </Grid>

      {isDeviceAdded && (
        <DeviceInfoFields<ReportFormValues> control={control} vendor={vendor ?? ''} />
      )}
      <Divider />
      <div>
        <TextWrapper
          mb="8px"
          text={getRequiredLabel('Upload Transmission Report')}
          variant="heading"
        />
        <Stack>
          <Dropzone
            onFilesSelected={onDrop}
            onPaste={() => {}}
            isUploading={isUploading} // Replace with a static value or adjust logic
            acceptedFileTypes="application/pdf"
            maxFiles={MAX_FILES}
          />
          {renderAttachments()}
        </Stack>
      </div>
    </>
  );
};

export default ManualTransmissionSection;
